<script lang="ts" setup>
import {
  type ImprintItem,
  NavigationBar,
  type NavigationBarItem, type ProfileItem,
} from '@teachino/shared-design-system';
import { computed, watch } from 'vue';
import { useSession } from '../stores/session';
import type { NavigationItem } from '../utils/NavigationItem';
import { $t } from '@teachino/shared-i18n';
import NavigationMobileTab
  from '../components/Navigation/Mobile/NavigationMobileTab.vue';
import { useRouter } from 'vue-router';
import { navigateTo } from 'nuxt/app';
import { useNavigationStore } from './navigation-store';
import { openFloatingChatDocked } from '@teachino/library-feature-ai-assistant';
import { openCreateCourseDialog } from '@teachino/library-feature-course';
import { useFeaturesManagement } from '../stores/featuresManagement';
import {
  isProductlaneEnabled,
  openProductlaneViewOrHelpPage,
} from '../utils/productlane';
import useCurrentUser from '../composables/useCurrentUser';
import { getPersonalPaywallGraceDayCounts, getPersonalTrialDayCounts } from '@teachino/shared-upgrade';
import { DateTime } from 'luxon';

const MAIN_MENU_MATERIALS_SIGNUP_CUTOFF = DateTime.fromISO(
  '2026-08-10T00:00:00.000Z',
);

const navigationStore = useNavigationStore();
const user = useCurrentUser();
const currentUser = user.getMeRef();
const sessionStore = useSession();
const username = sessionStore.getFullName;
const  $router  = useRouter();
const featuresStore = useFeaturesManagement();
const showProductlaneFeedback = isProductlaneEnabled();
sessionStore.init();

const showMobileTabBar = computed(() => {
  return  !$router.currentRoute.value.path.startsWith('/library/material/')
});

const trialDayCounts = computed(() =>
  getPersonalTrialDayCounts(sessionStore.getLicenses),
);

const graceDayCounts = computed(() =>
  getPersonalPaywallGraceDayCounts(sessionStore.getLicenses),
);

watch($router.currentRoute, (value) => {
  navigationStore.currentRoute = value.path;
}, {immediate: true})

const hasEuropathek = computed(() => {
  return sessionStore.hasActiveEuropathekIntegration;
})

const canSeeMainMenuMaterials = computed(() => {
  const accountCreatedAt = DateTime.fromISO(
    currentUser.value?.me?.createdAt ?? '',
  );

  return (
    user.hasPermission('main_menu_materials') &&
    accountCreatedAt.isValid &&
    accountCreatedAt < MAIN_MENU_MATERIALS_SIGNUP_CUTOFF
  );
});

const handleNewNavigation = async (href: string, openInNewTab: boolean) => {

  //todo - check for a better way to detect external links
  if(href.includes('http://') || href.includes('https://')) {
    await navigateTo(href, {
      external: true,
      open: {
        target: '_blank'
      }
    });
    return
  }
  if(openInNewTab) {
    await navigateTo(href, {
      open: {
        target: '_blank'
      }
    });
  }
  else  {
    await navigateTo(href);
  }
}

async function courseCreated(courseId: string, languageChanged: boolean) {
  try {
    await featuresStore.pushOnboardingProgressProperty('courseCreatedManually', true);
  } catch (_error) {
    // Keep flow non-blocking if onboarding progress update fails.
  }

  const coursePath = `/courses/${courseId}`;
  if (languageChanged) {
    window.location.href = coursePath;
    return;
  }
  await navigateTo(coursePath);
}

const openNewCourseDialog = async (): Promise<void> => {
  const result = await openCreateCourseDialog();
  if (!result.cancelled) {
    await courseCreated(result.value.courseId, result.value.languageChanged);
  }
};

const menuItems = computed(() => {
  const items: NavigationBarItem[] = [];

  items.push({
    id: 'timetable',
    type: 'menu-item',
    label: $t('features.timetable.page_timetable'),
    icon: 'calendar',
    href: '/timetable',
  });

  if (user.hasPermission('main_menu_ai_assistance')) {
    items.push({
      id: 'ai',
      type: 'menu-item',
      label: $t('ipi.ai_assistance'),
      icon: 'bot',
      onClick: () => openFloatingChatDocked(),
    });
  }

  items.push({
    id: 'course',
    type: 'menu-item',
    label: $t('course.all_courses'),
    icon: 'dashboard',
    href: '/course',
    exactMatch: true,
    trailingAction: {
      label: $t('navigation.new_course'),
      icon: 'plus',
      onClick: () => {
        void openNewCourseDialog();
      },
    },
  });

  items.push(...navigationStore.courseMenuItems);

  items.push({
    id: 'collections',
    type: 'menu-item',
    label: $t('navigation.collections'),
    icon: 'library',
    href: '/collections',
    active: navigationStore.isCollectionRoute,
  });

  if(canSeeMainMenuMaterials.value) {
    items.push({
      id: 'materials',
      type: 'menu-item',
      label: $t('navigation.library'),
      icon: 'files',
      href: '/library'
    });
  }

  if (hasEuropathek.value) {
    items.push({
      id: 'europathek',
      type: 'menu-item',
      label: $t('generic.europathek'),
      image: '/pictures/integrations/europathek.png',
      href: 'https://www.europathek.de',
    })
  }
  items.push({ id: 'separator', type: 'separator' });
  items.push({ id: 'help-label', type: 'label', label: $t('navigation.help_and_support') });
  items.push({
    id: 'help',
    type: 'menu-item',
    label: $t('generic.help'),
    icon: 'question_mark_circle',
    onClick: openHelp,
  });
  items.push({
    id: 'webinar',
    type: 'menu-item',
    label: $t('generic.webinar'),
    openInNewTab: true,
    icon: 'webinar',
    href: 'https://www.teachino.io/webinar',
  });
  if (showProductlaneFeedback) {
    items.push({
      id: 'feedback',
      type: 'menu-item',
      label: $t('navigation.feedback'),
      icon: 'speech_bubble',
      onClick: openFeedback,
    })
  }

  return items;
});

const profileMenuItems: ProfileItem[] = [
  { id: 'settings', href: '/account/profile', label: $t('navigation.profile_settings') },
  { id: 'license', href: '/account/upgrade', label: $t('navigation.licenses')},
  { id: 'integrations', href: '/account/integrations', label: $t('integration.settings_title') },
  { id: 'logout', href: '/auth/logout', label: $t('navigation.log_out'), isLogout: true },
];

const imprintMenuItems: ImprintItem[] = [
  { id: 'imprint', href: '/legal/imprint', label: $t('navigation.imprint') },
  { id: 'privacy', href: '/legal/privacy', label: $t('navigation.privacy') },
  { id: 'terms-conditions', href: '/legal/terms', label: $t('navigation.terms') },
  { id: 'server-status', href: 'https://status.teachino.io/', label: $t('navigation.status') },
  { id: 'company', label: '©Teachino FlexCo' },
];

const mobileNavigationItems = computed(() => {
  const items: NavigationItem[] = [];

  items.push({
    iconName: 'courses',
    label: $t('navigation.courses'),
    link: '/course',
    dataTestName: 'courses',
  });

  items.push({
    iconName: 'calendar',
    label: $t('navigation.timetable'),
    link: '/timetable',
    dataTestName: 'Timetable',
  });
  if (canSeeMainMenuMaterials.value) {
    items.push({
      iconName: 'library',
      label: $t('navigation.library'),
      link: '/library',
      dataTestName: 'library',
    });
  }
  items.push({
    iconName: 'account',
    label: $t('navigation.profile'),
    link: '/account',
    dataTestName: 'Profile',
  });

  return items;
});

const mobileNavigationGridClass = computed(() =>
  canSeeMainMenuMaterials.value ? 'grid-cols-4' : 'grid-cols-3',
);

function openFeedback() {
  void openProductlaneViewOrHelpPage('FEEDBACK');
}

function openHelp() {
  void openProductlaneViewOrHelpPage('INDEX');
}
</script>

<template>
  <div
    class="h-dvh overflow-hidden teachino-theme flex bg-white "
  >
    <div class="flex flex-1 min-w-0">
      <div
        class="border-slate-200 border-r hidden  bg-white-500 sm:flex justify-center "
      >
        <NavigationBar
          :current-route="navigationStore.currentRoute"
          :is-open="navigationStore.isOpen"
          :items="menuItems"
          :profile-items="profileMenuItems"
          :imprint-items="imprintMenuItems"
          :profile-label="$t('navigation.profile')"
          :username="username"
          :trial-days-left="trialDayCounts?.trialDaysLeft"
          :trial-days-total="trialDayCounts?.trialDaysTotal"
          :grace-days-left="graceDayCounts?.graceDaysLeft"
          :grace-days-total="graceDayCounts?.graceDaysTotal"
          @toggle-open="navigationStore.isOpen = !navigationStore.isOpen"
          @navigate-to="handleNewNavigation"
        />

      </div>
      <div class="app-page-scroll-viewport flex-1 overflow-auto">
        <slot class="flex flex-1" />
      </div>
    </div>
    <div
      v-if="showMobileTabBar"
      class=" fixed inset-x-0 bottom-0 z-40 h-16 w-full border-t border-neutral-200 bg-white-500/60 backdrop-blur-md sm:hidden"
      style="
            height: calc(4rem + env(safe-area-inset-bottom));
            padding-bottom: env(safe-area-inset-bottom);
      "
    >
      <div
        class="m-auto grid h-full max-w-120 content-evenly space-x-4 overflow-x-auto"
        :class="mobileNavigationGridClass"
      >
        <NavigationMobileTab
          v-for="item in mobileNavigationItems"
          :key="item.iconName"
          :item="item"
          :class="item.dataTestName"
        />
      </div>
    </div>
  </div>
</template>
